import React, { useState, useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from "lenis";
import { AnimatePresence, motion } from "framer-motion";

import Header from "@/components/Header";
import Footer from "@/components/Footer";
import Loader from "@/components/Loader";
import QuoteModal from "@/components/QuoteModal";
import MobileQuoteBar from "@/components/MobileQuoteBar";
import { QuoteProvider } from "@/context/QuoteContext";

import Home from "@/pages/Home";
import Products from "@/pages/Products";
import Industries from "@/pages/Industries";
import About from "@/pages/About";
import OurClients from "@/pages/OurClients";
import Contact from "@/pages/Contact";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const Page = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
  >
    {children}
  </motion.div>
);

function AnimatedRoutes() {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<Page><Home /></Page>} />
        <Route path="/products" element={<Page><Products /></Page>} />
        <Route path="/industries" element={<Page><Industries /></Page>} />
        <Route path="/about" element={<Page><About /></Page>} />
        <Route path="/clients" element={<Page><OurClients /></Page>} />
        <Route path="/contact" element={<Page><Contact /></Page>} />
      </Routes>
    </AnimatePresence>
  );
}

function App() {
  const [loading, setLoading] = useState(() => !sessionStorage.getItem("cx_visited"));

  useEffect(() => {
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const lenis = new Lenis({ lerp: 0.09, smoothWheel: true });
    let raf;
    const loop = (t) => { lenis.raf(t); raf = requestAnimationFrame(loop); };
    raf = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(raf); lenis.destroy(); };
  }, []);

  useEffect(() => {
    if (!loading) return;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, [loading]);

  const finishLoad = () => {
    sessionStorage.setItem("cx_visited", "1");
    document.body.style.overflow = "";
    setLoading(false);
  };

  return (
    <div className="App">
      <BrowserRouter>
        <QuoteProvider>
          <AnimatePresence>
            {loading && <Loader onDone={finishLoad} />}
          </AnimatePresence>
          <ScrollToTop />
          <Header />
          <main>
            <AnimatedRoutes />
          </main>
          <Footer />
          <div className="h-[76px] lg:hidden" aria-hidden="true" />
          <QuoteModal />
          <MobileQuoteBar />
        </QuoteProvider>
      </BrowserRouter>
    </div>
  );
}

export default App;
